import type { ReactElement } from 'react';
import type { SectionProps } from '../shared-types.js';
import { GenericSettingsSection } from './generic-settings-section.js';

/**
 * Device-registry section group rendered under the Control tab. It is NOT the
 * tab's own section-group name ('Control'): the registry still groups these
 * sections as 'Controls', and this tab is where they surface.
 */
const REGISTRY_SECTION_GROUP = 'Controls';

/**
 * `<ControlSection>` — the Control tab.
 *
 * This tab is the camera's own registry-derived `Controls` group (Pan Flip,
 * Tilt Flip, native presets, …) beside the generic video preview: the same
 * shape as the Video, Audio, Features and System tabs, and the surface
 * production shipped before the tab was promoted.
 *
 * It deliberately does NOT host the platform control settings (connection
 * type / IP / port form) or the PTZ pad. Those configure the Iris↔camera
 * control link and live ONLY on `Iris Connection → Control`
 * (../iris-connection/control-content.tsx). The two surfaces are distinct,
 * not a subset/superset pair; composing the protocol form and D-pad into this
 * tab as well was the duplication QA caught on the run-5 installer.
 *
 * The tab is withheld from the strip when this group has nothing visible
 * (Scaffold → useHasVisibleControls → orderSettingsTabs), so a camera with
 * nothing to show here gets no tab rather than one that opens on
 * "No settings available".
 */
export const ControlSection = (props: SectionProps): ReactElement => (
  <GenericSettingsSection
    {...props}
    previewTitle='Video preview'
    sectionGroupKey={REGISTRY_SECTION_GROUP}
  />
);
